/**
 * Contact form view for public site - NOS-55.
 * Renders form fields and submits to submit-contact-form edge function.
 */

import { useState, useCallback } from "react"
import { supabase } from "@/lib/supabase"
import { FunctionsHttpError } from "@supabase/supabase-js"
import type { FormField } from "./blocks/types"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export interface ContactFormViewProps {
  orgId: string
  sectionId?: string
  fields: FormField[]
  heading?: string
  body?: string
  submitLabel?: string
  primaryColor?: string
}

export function ContactFormView({
  orgId,
  sectionId,
  fields,
  heading = "Get in touch",
  body,
  submitLabel = "Send message",
  primaryColor = "var(--primary-color, #3b82f6)",
}: ContactFormViewProps) {
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const setFieldValue = useCallback((name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (loading || submitted) return

      const filtered: Record<string, string> = {}
      for (const f of fields) {
        const val = formData[f.name]?.trim()
        if (f.required && !val) {
          setError(`${f.label ?? f.name} is required`)
          return
        }
        if (val !== undefined) filtered[f.name] = val
      }

      setLoading(true)
      setError(null)

      try {
        const { error: fnError } = await supabase.functions.invoke(
          "submit-contact-form",
          {
            body: {
              org_id: orgId,
              form_data: filtered,
              page_section_id: sectionId || undefined,
            },
          }
        )

        if (fnError) {
          let message = fnError.message ?? "Failed to send message"
          if (fnError instanceof FunctionsHttpError && fnError.context) {
            try {
              const res = fnError.context as Response
              const body = await res.json() as { error?: string }
              if (typeof body?.error === "string") message = body.error
            } catch {
              // keep default message
            }
          }
          setError(message)
          return
        }

        setSubmitted(true)
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unexpected error occurred")
      } finally {
        setLoading(false)
      }
    },
    [orgId, sectionId, fields, formData, loading, submitted]
  )

  if (submitted) {
    return (
      <div className="space-y-4">
        {heading && (
          <h2 className="text-2xl font-bold" style={{ color: primaryColor }}>
            {heading}
          </h2>
        )}
        <p className="text-muted-foreground rounded-lg bg-green-50 dark:bg-green-950/30 p-4">
          Thank you for your message. We&apos;ll get back to you soon.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {heading && (
        <h2 className="text-2xl font-bold" style={{ color: primaryColor }}>
          {heading}
        </h2>
      )}
      {body && <p className="text-muted-foreground">{body}</p>}
      <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
        {fields.map((f) => (
          <div key={f.name} className="space-y-2">
            <Label htmlFor={f.name}>
              {f.label ?? f.name}
              {f.required && (
                <span className="text-destructive ml-0.5">*</span>
              )}
            </Label>
            {f.type === "textarea" ? (
              <Textarea
                id={f.name}
                name={f.name}
                value={formData[f.name] ?? ""}
                onChange={(e) => setFieldValue(f.name, e.target.value)}
                required={f.required ?? false}
                rows={4}
                className="resize-none"
              />
            ) : f.type === "select" ? (
              <Select
                value={formData[f.name] ?? ""}
                onValueChange={(v) => setFieldValue(f.name, v)}
                required={f.required ?? false}
              >
                <SelectTrigger id={f.name}>
                  <SelectValue placeholder={`Select ${f.label ?? f.name}`} />
                </SelectTrigger>
                <SelectContent>
                  {(f.options ?? []).map((opt, i) => (
                    <SelectItem key={i} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id={f.name}
                name={f.name}
                type={f.type === "email" ? "email" : "text"}
                value={formData[f.name] ?? ""}
                onChange={(e) => setFieldValue(f.name, e.target.value)}
                required={f.required ?? false}
              />
            )}
          </div>
        ))}
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={loading}
          style={{ backgroundColor: primaryColor }}
        >
          {loading ? "Sending..." : submitLabel}
        </Button>
      </form>
    </div>
  )
}
